/* sb-dc-upload.js - print file goes straight to storage. 2026-09-03
   v7, 2026-09-18: waits for a pre-upload that is still in flight instead of
   racing it, and cancels it when it will not be used (see RACING).

   Why this file exists: every failed add to cart on 9/3 (33 POSTs, all HTTP
   546) was the dc-print-upload Edge Function dying with "Memory limit
   exceeded". The cart module in sb-dc-aug.js sends the print PNG as base64
   inside a JSON body, and the function decoded the whole thing in memory.
   Blank canvases and small stickers fit; any banner with real artwork did not.

   The 328 KB bundle cannot be re-uploaded through the API, so this wraps
   window.fetch and rewrites that one request into three that never put the
   file through the function:

     1. POST dc-print-upload {action:'sign'}      -> signed storage upload URL
     2. PUT  <signed URL> with the PNG Blob        -> Supabase Storage directly
     3. POST dc-print-upload {action:'finalize'}  -> size + PNG-magic check, JSON,
                                                     deleteToken, printUrl

   Step 3 returns the same JSON shape the old function did, so the bundle
   cannot tell the difference. If any step fails the original request is sent
   as-is. window.__dcUploadPath says which path the last upload took.

   FAST PATH
   sb-dc-preupload.js renders and uploads the same file while the customer
   reads the proof, under a temporary id, and records the SHA-256 of the base64
   of those bytes. Here:

     length differs      -> normal path, nothing hashed
     length matches      -> hash the base64 string we were handed (0.13s on
                            38MB; no decode, because the bundle gives us a
                            string and the comparison value is a string)
     hash matches        -> {action:'promote'}: the function copies the object
                            onto the real design id server-side. No sign, no
                            decode, no second 38MB transfer.
     anything else       -> sign / decode / PUT / finalize, exactly as before.

   Measured on an 8x4ft board with a photo: 11.0s before any of this, 4.95s
   with promote.

   RACING (fixed 2026-09-18)
   A 38MB pre-upload takes 13-15s. If the customer approved while it was still
   sending, this used to start the bundle's own 38MB upload alongside it: two
   transfers splitting one uplink, and an add that took 23s - worse than never
   pre-uploading at all.

   So when window.__dcPreInFlight exists, this waits for it (up to WAIT_MS -
   it is usually seconds from done, and those seconds are not wasted: they are
   the same file arriving). If it finishes in time the fast path is taken. If
   it fails, or the hash does not match, or the wait runs out, its abort() is
   called first so the uplink is free before the normal upload starts.

   The comparison is SHA-256 over the whole file, so what ends up on the order
   is always the bundle's own render of the approved design, or a byte-identical
   copy.

   PROGRESS
   The PUT is an XHR because XHR reports upload progress and fetch does not.
   Each chunk dispatches window 'sbdc:progress' {phase:'upload', loaded, total},
   which sb-dc-addprogress.js turns into the real part of its bar.

   window.__dcUploadStats carries the per-phase timings of the last upload. */
(function () {
  'use strict';

  var FN = 'https://uktrkymhgmzlufzxsavf.supabase.co/functions/v1/dc-print-upload';
  var WAIT_MS = 25000;       // how long an in-flight pre-upload may be waited on
  var orig = window.fetch;

  function blank() {
    return { signMs: 0, decodeMs: 0, hashMs: 0, waitMs: 0, putMs: 0, promoteMs: 0,
             finalizeMs: 0, totalMs: 0, mb: 0, savedMs: 0, usedPreUpload: false };
  }
  window.__dcUploadStats = blank();

  function now() { return (window.performance && performance.now()) || Date.now(); }

  function say(detail) {
    try { window.dispatchEvent(new CustomEvent('sbdc:progress', { detail: detail })); }
    catch (e) {}
  }

  function headerGet(h, name) {
    try {
      if (!h) return null;
      if (typeof h.get === 'function') return h.get(name);
      var keys = Object.keys(h);
      for (var i = 0; i < keys.length; i++) if (keys[i].toLowerCase() === name) return h[keys[i]];
    } catch (e) {}
    return null;
  }

  function b64ToBlob(b64) {
    return orig.call(window, 'data:image/png;base64,' + b64).then(function (r) { return r.blob(); });
  }

  function sha256Text(s) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(s)).then(function (d) {
      var a = new Uint8Array(d), out = '';
      for (var i = 0; i < a.length; i++) out += a[i].toString(16).padStart(2, '0');
      return out;
    });
  }

  function call(action, body, signal, extraHeaders) {
    var headers = Object.assign({ 'content-type': 'application/json' }, extraHeaders || {});
    return orig.call(window, FN, {
      method: 'POST', headers: headers,
      body: JSON.stringify(Object.assign({ action: action }, body)), signal: signal
    });
  }

  function mustJson(r, what) {
    return r.text().then(function (t) {
      var j = null;
      try { j = t ? JSON.parse(t) : null; } catch (e) {}
      if (!r.ok || !j || !j.ok) {
        var e = new Error(what + ' HTTP ' + r.status + ' ' + String(t || '').slice(0, 160));
        e.status = r.status;
        throw e;
      }
      return j;
    });
  }

  /* PUT by XHR, purely so upload progress exists. */
  function putWithProgress(url, blob, signal) {
    return new Promise(function (resolve, reject) {
      var x = new XMLHttpRequest();
      x.open('PUT', url, true);
      x.setRequestHeader('content-type', 'image/png');
      x.setRequestHeader('x-upsert', 'true');
      if (x.upload) {
        x.upload.onprogress = function (e) {
          if (e && e.lengthComputable) say({ phase: 'upload', loaded: e.loaded, total: e.total });
        };
      }
      x.onload = function () {
        resolve({ ok: x.status >= 200 && x.status < 300, status: x.status,
                  text: function () { return Promise.resolve(x.responseText || ''); } });
      };
      x.onerror = function () { reject(new Error('storage PUT network error')); };
      x.onabort = function () { var e = new Error('aborted'); e.name = 'AbortError'; reject(e); };
      if (signal) {
        if (signal.aborted) { x.abort(); return; }
        signal.addEventListener('abort', function () { try { x.abort(); } catch (e) {} });
      }
      x.send(blob);
    });
  }

  /* If a pre-upload is mid-flight, let it finish: the bytes it is sending are
     the ones this add needs, and starting a second copy would just halve the
     uplink for both. */
  function waitForPre(S) {
    var f = window.__dcPreInFlight;
    if (!f || !f.promise) return Promise.resolve();
    var t0 = now();
    say({ phase: 'upload', loaded: 1, total: 100 });
    return Promise.race([
      f.promise.catch(function () {}),
      new Promise(function (res) { setTimeout(res, WAIT_MS); })
    ]).then(function () {
      S.waitMs = Math.round(now() - t0);
    });
  }

  function dropPre() {
    var f = window.__dcPreInFlight;
    if (f && typeof f.abort === 'function') f.abort();   // give the uplink back
    window.__dcPreInFlight = null;
  }

  window.fetch = function (input, init) {
    var self = this, args = arguments;
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    var raw = init && init.body;
    if (url !== FN || method !== 'POST' || typeof raw !== 'string' || raw.indexOf('"printBase64"') < 0) {
      return orig.apply(self, args);
    }

    var p = null;
    try { p = JSON.parse(raw); } catch (e) {}
    if (!p || !p.printBase64 || !p.designId) return orig.apply(self, args);

    var b64 = p.printBase64;
    p.printBase64 = null;
    var signal = init && init.signal;
    var idem = headerGet(init && init.headers, 'x-idempotency-key') || p.idempotencyKey || null;
    var side = p.side === 'back' ? 'back' : 'front';

    var S = window.__dcUploadStats = blank();
    var t0 = now();

    var startP = (side === 'front') ? waitForPre(S) : Promise.resolve();

    return startP.then(function () {
      var pre = (side === 'front') ? window.__dcPreReady : null;

      /* Cheap gate first: a different length cannot be the same file. */
      if (!pre || !pre.b64hash || pre.b64len !== b64.length) return false;
      var tH = now();
      return sha256Text(b64).then(function (h) {
        S.hashMs = Math.round(now() - tH);
        return h === pre.b64hash;
      }).catch(function () { return false; });
    }).then(function (match) {
      var pre = window.__dcPreReady;
      if (match && pre) {
        /* Server-side copy: nothing decoded, nothing signed, and the bytes
           never leave the bucket. */
        S.usedPreUpload = true;
        S.mb = +(pre.size / 1048576).toFixed(2);
        b64 = null;
        say({ phase: 'upload', loaded: 1, total: 1 });
        say({ phase: 'finalize' });
        var tPro = now();
        window.__dcUploadPath = 'promote';
        return call('promote', {
          designId: p.designId, side: side, fromDesignId: pre.tempId,
          designJson: p.designJson || null, idempotencyKey: idem
        }, signal, idem ? { 'x-idempotency-key': idem } : null).then(function (r) {
          S.promoteMs = Math.round(now() - tPro);
          S.totalMs = Math.round(now() - t0);
          window.__dcPreReady = null;      // consumed
          say({ phase: 'cart' });
          return r;
        });
      }

      /* Not usable - make sure nothing of ours is still on the wire before
         the real upload starts. */
      dropPre();

      var tSign = now();
      var signP = call('sign', { designId: p.designId, side: side }, signal)
        .then(function (r) { return mustJson(r, 'sign'); })
        .then(function (s) { S.signMs = Math.round(now() - tSign); return s; });

      var tDec = now();
      var blobP = b64ToBlob(b64).then(function (blob) {
        S.decodeMs = Math.round(now() - tDec);
        S.mb = +(blob.size / 1048576).toFixed(2);
        b64 = null;
        return blob;
      });

      return Promise.all([signP, blobP]).then(function (both) {
        var s = both[0], blob = both[1];
        if (!s.uploadUrl) throw new Error('sign: no uploadUrl');
        S.savedMs = Math.min(S.signMs, S.decodeMs);
        say({ phase: 'upload', loaded: 0, total: blob.size });
        var tPut = now();
        return putWithProgress(s.uploadUrl, blob, signal).then(function (r) {
          S.putMs = Math.round(now() - tPut);
          return r;
        });
      }).then(function (r) {
        if (r.ok) return null;
        return r.text().then(function (t) {
          var e = new Error('storage PUT HTTP ' + r.status + ' ' + String(t || '').slice(0, 160));
          e.status = r.status;
          throw e;
        });
      }).then(function () {
        var h = {};
        if (idem) h['x-idempotency-key'] = idem;
        window.__dcUploadPath = 'direct';
        say({ phase: 'finalize' });
        var tFin = now();
        return call('finalize', {
          designId: p.designId, side: side,
          designJson: p.designJson || null, idempotencyKey: idem
        }, signal, h).then(function (r) {
          S.finalizeMs = Math.round(now() - tFin);
          S.totalMs = Math.round(now() - t0);
          say({ phase: 'cart' });
          return r;
        });
      });
    }).catch(function (e) {
      if (e && e.name === 'AbortError') throw e;
      try { console.warn('[sb-dc-upload] direct upload failed, using the old path:', e && e.message); } catch (x) {}
      window.__dcUploadPath = 'legacy: ' + (e && e.message || '');
      b64 = null;
      return orig.apply(self, args);
    });
  };
})();
